import { RouterLink as Link } from "../../router";
import { Card, CardContent, CardHeader, CardTitle } from "../../card";
import type { HelpCategoryRow } from "../../types/help";

export interface HelpCategoryGridItem extends HelpCategoryRow {
  article_count: number;
}

export interface HelpCategoryGridProps {
  categories: HelpCategoryGridItem[];
}

/**
 * Grid of category cards on the /help landing. Each card links to
 * `/help/<slug>` and shows the description plus article count. Icons
 * are rendered as lucide names; renderer is shell-side so we accept
 * the icon string and let the consumer resolve it (the page wraps it
 * with a lucide lookup). Here we render text only.
 */
export function HelpCategoryGrid({ categories }: HelpCategoryGridProps) {
  if (categories.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center type-body text-muted-foreground">
          No categories yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {categories.map((cat) => (
        <Link
          key={cat.id}
          href={`/help/${cat.slug}`}
          className="block rounded-md transition-colors"
        >
          <Card className="h-full hover:bg-secondary/30">
            <CardHeader>
              <CardTitle className="type-title">{cat.name}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {cat.description ? (
                <p className="type-body text-muted-foreground">{cat.description}</p>
              ) : null}
              <p className="type-body text-muted-foreground">
                {cat.article_count}{" "}
                {cat.article_count === 1 ? "article" : "articles"}
              </p>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}
